// Thin typed fetch wrapper for the backend API (same-origin via Vite proxy).

export interface Health {
  status: string;
  version: string;
  crs_default: number;
}

export interface Project {
  id: number;
  name: string;
  client: string | null;
  slug: string;
  source_root: string | null;
  crs_epsg: number;
}

export interface NodeAsset {
  id: number;
  kind: string;
  dimension: string;
  role: string;
  web_path: string;
  status: string;
  updated: string | null; // ingest/convert time = layer "last updated"
  style?: Record<string, unknown> | null; // 2D symbology from the project .lyrx
  radius?: number | null; // 3D model half-diagonal (metres), for accurate fly-to framing
  icon?: { web_path: string; kind: string; point: boolean } | null; // lyrx legend icon
}

export interface FloodScenario {
  asset_id: number;
  name: string;
  return_period: number; // % AEP
  year: number;
  variable: string; // Depth | Level | Velocity
  web_path: string; // /api/tiles/{id}/{z}/{x}/{y}.png
  bbox: { minx: number | null; miny: number | null; maxx: number | null; maxy: number | null };
}

export interface AuditEntry {
  id: number;
  entity: string;
  action: string;
  field: string | null;
  old: string | null;
  new: string | null;
  actor: string | null;
  at: string | null;
  item_id: number | null;
  detail: Record<string, unknown> | null;
}

export type ReviewState = "not_started" | "approved" | "needs_work" | "rejected" | "excluded";

export interface Review {
  status_2d: ReviewState;
  notes_2d: string | null;
  reviewed_2d_at: string | null;
  status_3d: ReviewState;
  notes_3d: string | null;
  reviewed_3d_at: string | null;
  reviewer: string | null;
}

export interface RegisterNode {
  id: number;
  parent_id: number | null;
  node_type: "category" | "item";
  review: Review | null;
  layer_name: string | null;
  data_3d_provided: string | null;
  file_name_2d: string | null;
  related_3d_name: string | null;
  is_conformed: string | null;
  uploaded_ipad: boolean;
  uploaded_desktop: boolean;
  confirmed_with_fortescue: string | null;
  status_sheet_status: string | null;
  sort_order: number;
  assets: NodeAsset[];
  children: RegisterNode[];
}

export interface Asset {
  id: number;
  name: string;
  kind: string;
  dimension: string;
  web_path: string;
  status: string;
  crs_epsg: number | null;
  source_datum: string | null;
  lon: number | null;
  lat: number | null;
  height: number | null;
  bbox: { minx: number | null; miny: number | null; maxx: number | null; maxy: number | null };
  placement_matrix: number[] | null;
  style?: Record<string, unknown> | null; // 2D symbology from the project .lyrx
  radius?: number | null; // 3D model half-diagonal (metres), for accurate fly-to framing
  icon?: { web_path: string; kind: string; point: boolean } | null; // lyrx legend icon
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    ...init,
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

export const api = {
  health: () => req<Health>("/api/health"),
  me: () => req<{ user: string; auth: boolean; role: string }>("/api/auth/me"),
  login: (username: string, password: string) =>
    req<{ user: string; role: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () => req<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  listProjects: () => req<Project[]>("/api/projects"),
  createProject: (body: { name: string; client?: string }) =>
    req<Project>("/api/projects", { method: "POST", body: JSON.stringify(body) }),
  importRegister: (projectId: number, path: string, sheet_name?: string) =>
    req<Record<string, unknown>>(`/api/projects/${projectId}/register/import`, {
      method: "POST",
      body: JSON.stringify({ path, sheet_name }),
    }),
  registerTree: (projectId: number) =>
    req<{ project_id: number; tree: RegisterNode[] }>(
      `/api/projects/${projectId}/register/tree`,
    ),
  listAssets: (projectId: number) =>
    req<{ assets: Asset[] }>(`/api/projects/${projectId}/assets`),
  scanFolder: (projectId: number, root: string, recurse: boolean) =>
    req<ScanResult>(`/api/projects/${projectId}/ingest/scan`, {
      method: "POST",
      body: JSON.stringify({ root, recurse }),
    }),
  convertItems: (projectId: number, items: ConvertItem[]) =>
    req<{ jobs: Job[] }>(`/api/projects/${projectId}/ingest/convert`, {
      method: "POST",
      body: JSON.stringify({ items }),
    }),
  listJobs: (projectId: number) =>
    req<{ jobs: Job[] }>(`/api/projects/${projectId}/jobs`),
  autoMatch: (projectId: number, registerPath: string) =>
    req<{ linked_existing: number; created: number; unmatched: number }>(
      `/api/projects/${projectId}/ingest/automatch`,
      { method: "POST", body: JSON.stringify({ register_path: registerPath }) },
    ),
  cancelJob: (projectId: number, jobId: number) =>
    req<Job>(`/api/projects/${projectId}/jobs/${jobId}/cancel`, { method: "POST" }),
  listMarkups: (projectId: number) =>
    req<{ markups: MarkupOut[] }>(`/api/projects/${projectId}/markups`),
  createMarkup: (projectId: number, body: MarkupIn) =>
    req<MarkupOut>(`/api/projects/${projectId}/markups`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteMarkup: (projectId: number, markupId: number) =>
    fetch(`/api/projects/${projectId}/markups/${markupId}`, { method: "DELETE" }),
  patchMarkup: (
    projectId: number,
    markupId: number,
    body: { resolved?: boolean; comment?: string; actor?: string | null },
  ) =>
    req<MarkupOut>(`/api/projects/${projectId}/markups/${markupId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  saveScreenshot: (projectId: number, imageBase64: string, caption?: string, cameraJson?: string) =>
    req<{ id: number; path: string }>(`/api/projects/${projectId}/screenshots`, {
      method: "POST",
      body: JSON.stringify({ image_base64: imageBase64, caption, camera_json: cameraJson }),
    }),
  listUnlinked: (projectId: number) =>
    req<{ assets: Asset[] }>(`/api/projects/${projectId}/assets/unlinked`),
  linkAsset: (projectId: number, assetId: number, registerItemId: number) =>
    req<{ linked: boolean }>(`/api/projects/${projectId}/assets/${assetId}/link`, {
      method: "POST",
      body: JSON.stringify({ register_item_id: registerItemId }),
    }),
  unlinkAsset: (projectId: number, assetId: number) =>
    req<{ unlinked: boolean }>(`/api/projects/${projectId}/assets/${assetId}/unlink`, {
      method: "POST",
    }),
  setReview: (
    projectId: number,
    itemId: number,
    body: { dimension: "2d" | "3d"; status: string; notes?: string | null; reviewer?: string | null },
  ) =>
    req<{ item_id: number; review: Review }>(`/api/projects/${projectId}/items/${itemId}/review`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  // Lightweight item_id -> review map, for near-live polling (no full tree re-fetch).
  getReviews: (projectId: number) =>
    req<{ reviews: Record<number, Review | null> }>(`/api/projects/${projectId}/reviews`),
  // Convert a projected easting/northing (default MGA Zone 50) to lon/lat for "go to".
  toLonLat: (e: number, n: number, epsg = 28350) =>
    req<{ lon: number; lat: number }>(`/api/util/to-lonlat?e=${e}&n=${n}&epsg=${epsg}`),
  // History trail (newest first); pass itemId for a single layer's events.
  getAudit: (projectId: number, itemId?: number) =>
    req<{ audit: AuditEntry[] }>(
      `/api/projects/${projectId}/audit${itemId != null ? `?item_id=${itemId}` : ""}`,
    ),
  // Per-layer comment threads (both 2D and 3D), oldest first.
  getItemComments: (projectId: number, itemId: number) =>
    req<{ comments: MarkupOut[] }>(`/api/projects/${projectId}/items/${itemId}/comments`),
  // Terrain DEMs (Cesium runs one at a time -> the view bar offers a picker).
  terrains: (projectId: number) =>
    req<{ terrains: { id: number; name: string; web_path: string }[] }>(
      `/api/projects/${projectId}/terrains`,
    ),
  // Flood/simulation scenario catalog (return period x year x variable).
  floodScenarios: (projectId: number) =>
    req<{
      scenarios: FloodScenario[];
      return_periods: number[];
      years: number[];
      variables: string[];
    }>(`/api/projects/${projectId}/flood-scenarios`),
  adoptAsset: (projectId: number, assetId: number) =>
    req<{ item_id: number; name: string }>(`/api/projects/${projectId}/assets/${assetId}/adopt`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  exportRegister: (projectId: number) =>
    req<{ path: string; file: string }>(`/api/projects/${projectId}/export/register`, { method: "POST" }),
  exportReport: (projectId: number) =>
    req<{ html_url: string; pdf_url: string | null; pdf_path: string | null; html_path: string }>(
      `/api/projects/${projectId}/export/report`,
      { method: "POST" },
    ),
};

export interface ScanItem {
  path: string;
  name: string;
  ext: string;
  size_mb: number;
  dimension: string;
  target_kind: string;
  has_offset: boolean;
  offset: number[] | null;
  source_epsg: number;
  match_item_id: number | null;
  match_item_name: string | null;
  cached: boolean;
  convertible: boolean;
}

export interface ScanResult {
  root: string;
  recurse: boolean;
  count: number;
  matched: number;
  items: ScanItem[];
}

export interface ConvertItem {
  path: string;
  source_epsg?: number | null;
  offset?: number[] | null;
  register_item_id?: number | null;
  role?: string | null;
  decimate?: number;
}

export interface Job {
  id: number;
  kind: string;
  status: string;
  progress: number;
  label: string | null;
  message: string | null;
  target_asset_id: number | null;
}

export interface MarkupIn {
  type: string;
  geometry_json?: string | null;
  label?: string | null;
  comment?: string | null;
  camera_json?: string | null;
  style_json?: string | null;
  register_item_id?: number | null;
  author?: string | null;
  dimension?: "2D" | "3D" | null;
}

export interface MarkupOut {
  id: number;
  type: string;
  geometry: GeoJSON.Geometry | null;
  label: string | null;
  comment: string | null;
  camera: CameraState | null;
  style: Record<string, unknown> | null;
  register_item_id: number | null;
  author: string | null;
  dimension: "2D" | "3D" | null;
  resolved: boolean;
  resolved_at: string | null;
  created_at: string | null;
}

export interface CameraState {
  lon: number;
  lat: number;
  height: number;
  heading: number;
  pitch: number;
  roll: number;
}

declare global {
  namespace GeoJSON {
    interface Geometry {
      type: string;
      coordinates: unknown;
    }
  }
}
